/**
 * A verdict, and the criteria that produced it (spec: surface expectations,
 * Task 13).
 *
 * The chip OPENS. A chip that says FAIL and nothing else starts an argument
 * with the contractor; the criteria list settles it by showing which check
 * failed, by how much, and against what.
 *
 * Every severity carries a glyph as well as a colour. Colour alone fails for
 * a red-green colour deficiency and on a greyscale print — already this
 * codebase's stated rule (`server/background/src/functions/processReport/stationSectionSvg.ts:6`,
 * "hatched as well as coloured so a greyscale print still reads").
 *
 * `SEVERITY` is exported (not local) because Task 14's per-bin verdict cell
 * in the station-volumes table reuses the same glyph + colour map — a bin
 * amber in the table and red in this header would be worse than no colour
 * at all.
 *
 * TWIN: `VERDICT_GLYPH` in
 * `server/background/src/functions/processReport/types.ts`. This map is the
 * SINGLE SOURCE for the glyphs (owner ruling); that one is the PDF's copy,
 * separate only because the report worker must not import a React module,
 * and it carries one extra row (`PENDING`, a state rather than a severity).
 * The two disagreed on MARGINAL until the crossfall check printed the same
 * band's mark in the dock's section label and in the PDF's. Changing a glyph
 * here means changing it there; the report's `__tests__/types.test.ts` reads
 * THIS file as text and fails if they drift.
 */
import type { Verdict } from '@gis/clearance';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { mToMm } from '@/lib/surfaceExpectation';
import { cn } from '@/lib/utils';

export const SEVERITY = {
  PASS: { glyph: '✓', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  MARGINAL: { glyph: '~', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  FAIL: { glyph: '✗', cls: 'bg-red-500/15 text-red-700 dark:text-red-400' },
  INCONCLUSIVE: { glyph: '?', cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-400' },
} as const satisfies Record<Verdict, { glyph: string; cls: string }>;

/** The verdict scale is defined in `@gis/clearance` (shared with the report
 *  worker, which must not import this React module); re-exported here so
 *  every `@/components/VerdictChip` import of `Verdict` keeps working. */
export type { Verdict };

/**
 * The only four criterion keys `evaluate()` ever produces. `meanOffset`
 * (MATCH only) and `meanThickness` (CONSTANT/SURFACES only) are mutually
 * exclusive by mode; `outOfToleranceArea` and `coverage` are always present.
 * There is no `crossings` criterion — crossings are counted into
 * `outOfToleranceArea`'s budget, not judged on their own.
 */
export type CriterionKey = 'meanOffset' | 'meanThickness' | 'outOfToleranceArea' | 'coverage';

export interface Criterion {
  key: CriterionKey;
  pass: boolean;
  actual: number;
  required: number;
}

/**
 * `outOfToleranceArea` and `coverage` are already percentages of measured
 * area. `meanOffset` and `meanThickness` are metres on the wire — repo
 * convention: thicknesses are metres on the wire, millimetres on screen, so
 * they are converted here and never printed raw (e.g. `0.3875`).
 */
const PCT_KEYS = new Set(['outOfToleranceArea', 'coverage']);

const fmtCriterion = (key: string, v: number): string =>
  PCT_KEYS.has(key) ? `${v.toFixed(1)} %` : `${mToMm(v).toFixed(1)} mm`;

/**
 * A criterion row someone else has already formatted. `Criterion` is typed to
 * the surface-expectation checks and formats its own figures (mm / %); a
 * verdict from another analysis — clearance counts points, not millimetres —
 * brings its rows as strings instead and keeps the same chip, glyphs and
 * colours. `severity` rather than `pass`: a clearance near-miss is MARGINAL,
 * not FAIL, and painting it red would overstate it.
 */
export interface VerdictRow {
  key: string;
  label: string;
  actual: string;
  /** Omitted when the row has no threshold to print beside the figure. */
  required?: string;
  severity: Verdict;
  /**
   * `'precondition'`: a row that says whether there is anything to judge
   * (clearance's "Points scored"), not a test the run passed. Drawn neutral
   * with ⓘ instead of a green ✓ — the audit (A8) read "✓ Points scored" as a
   * passed check — unless it is unmet (`severity: 'INCONCLUSIVE'`), when it
   * carries the verdict's own `?`, because then it IS the reason. Defaults to
   * `'test'`; `severity` keeps its meaning either way, so `data-severity` on
   * the row stays what callers and tests already read.
   */
  kind?: 'test' | 'precondition';
}

export interface VerdictChipProps {
  verdict: Verdict;
  /** Surface-expectation criteria (formatted here). */
  criteria?: Criterion[];
  /** Preformatted rows, for a verdict that is not a surface expectation.
   *  Rendered after `criteria` when both are given. */
  rows?: VerdictRow[];
  /** The closing line of the list. Defaults to the surface-expectation
   *  advisory unless `rows` are given (an empty `criteria` list still gets
   *  it), and to nothing for `rows` — that advisory is about coring, which
   *  says nothing about any other check. */
  footnote?: string;
  /** Tooltip on the chip itself — e.g. the rule that produced the verdict. */
  title?: string;
}

/**
 * Only the status mark and the measured figure carry the row's colour (A8: a
 * pink slab and an amber slab under two rows read as alarm, and the reader
 * could not tell the figure from the limit). The glyph is the non-colour cue.
 * Kept beside `SEVERITY`, not inside it: the report's twin test parses that
 * map's lines as text.
 */
const SEVERITY_INK: Record<Verdict, string> = {
  PASS: 'text-emerald-700 dark:text-emerald-400',
  MARGINAL: 'text-amber-700 dark:text-amber-400',
  FAIL: 'text-red-700 dark:text-red-400',
  INCONCLUSIVE: 'text-slate-600 dark:text-stone-300',
};

const NEUTRAL_INK = 'text-slate-500 dark:text-stone-400';

interface TableRow {
  key: string;
  label: string;
  actual: string;
  required?: string;
  severity: Verdict;
  precondition: boolean;
  /** `data-pass` for the surface-expectation criteria (their tests read it). */
  pass?: boolean;
}

export function VerdictChip({ verdict, criteria = [], rows = [], footnote, title }: VerdictChipProps) {
  const { t } = useTranslation('project');
  const [open, setOpen] = useState(false);
  const s = SEVERITY[verdict];
  const lines: TableRow[] = [
    ...criteria.map((c) => ({
      key: c.key,
      label: t(`expectation.criterion.${c.key}`),
      actual: fmtCriterion(c.key, c.actual),
      required: fmtCriterion(c.key, c.required),
      severity: (c.pass ? 'PASS' : 'FAIL') as Verdict,
      precondition: false,
      pass: c.pass,
    })),
    ...rows.map((r) => ({ ...r, precondition: r.kind === 'precondition' })),
  ];
  const note = footnote ?? (!rows.length ? t('expectation.advisory') : null);

  return (
    // Radix Popover, not a hand-placed absolute list (A8): the list used to
    // open straight down over the toolbar it was opened from and be clipped
    // by any `overflow-hidden` ancestor. Portalled, it lands below the chip,
    // end-aligned so it grows INTO the dock or card rather than off its edge,
    // and collision padding flips it rather than covering the trigger row.
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          data-testid='verdict-chip'
          title={title}
          className={cn(
            'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none',
            s.cls,
          )}>
          <span aria-hidden='true'>{s.glyph}</span>
          {t(`expectation.verdict.${verdict}`)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid='verdict-criteria'
        side='bottom'
        align='end'
        sideOffset={6}
        collisionPadding={8}
        aria-label={t(`expectation.verdict.${verdict}`)}
        className='sv-palette w-[21rem] border-slate-200 bg-white/95 p-0 text-xs text-slate-700 backdrop-blur dark:border-stone-700 dark:bg-stone-900/95 dark:text-stone-200'>
        {lines.length > 0 && (
          <table className='w-full border-collapse'>
            {/* The column heads A8 asked for: an unlabelled "7 / 0" left the
                reader to guess which figure was measured and which was the
                limit. */}
            <thead>
              <tr className='border-b border-slate-200 text-[11px] leading-4 text-slate-500 dark:border-stone-700 dark:text-stone-400'>
                <th scope='col' className='py-1.5 pr-2 pl-3 text-left font-medium' colSpan={2}>
                  {t('expectation.criteriaTable.test')}
                </th>
                <th scope='col' className='px-1.5 py-1.5 text-right font-medium'>
                  {t('expectation.criteriaTable.measured')}
                </th>
                <th scope='col' className='py-1.5 pr-3 pl-1.5 text-right font-medium'>
                  {t('expectation.criteriaTable.limit')}
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((r) => {
                // A met precondition is neutral; an unmet one is the reason the
                // run is INCONCLUSIVE and keeps that verdict's mark.
                const neutral = r.precondition && r.severity !== 'INCONCLUSIVE';
                const ink = neutral ? NEUTRAL_INK : SEVERITY_INK[r.severity];
                return (
                  <tr
                    key={r.key}
                    data-testid='verdict-criterion'
                    data-severity={r.severity}
                    data-pass={r.pass === undefined ? undefined : String(r.pass)}
                    data-kind={r.precondition ? 'precondition' : undefined}
                    className='align-baseline'>
                    <td className={cn('w-4 py-1 pl-3 text-center text-[11px] leading-4 font-semibold', ink)}>
                      <span aria-hidden='true'>{neutral ? 'ⓘ' : SEVERITY[r.severity].glyph}</span>
                      {/* Colour is never the only cue (file header), and a
                          glyph hidden from assistive tech needs a text
                          equivalent — without it a screen-reader user hears
                          the test and both numbers but never whether it
                          passed, the one fact this row exists to convey. */}
                      <span className='sr-only'>
                        {neutral ? t('expectation.criteriaTable.precondition') : t(`expectation.verdict.${r.severity}`)}
                      </span>
                    </td>
                    <td className='py-1 pr-1 pl-1.5 leading-4'>{r.label}</td>
                    <td className={cn('px-1.5 py-1 text-right font-medium whitespace-nowrap tabular-nums', ink)}>
                      {r.actual}
                    </td>
                    <td className='py-1 pr-3 pl-1.5 text-right whitespace-nowrap text-slate-500 tabular-nums dark:text-stone-400'>
                      {r.required ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Design doc §6.3: v0 has no virtual coring, so the verdict cannot
            claim conformance with a core-based specification. Said here,
            where the verdict is read, not in a footnote nobody opens. */}
        {note && (
          <p
            className={cn(
              'px-3 py-2 text-[11px] leading-4 text-slate-500 dark:text-stone-400',
              lines.length > 0 && 'border-t border-slate-200 dark:border-stone-700',
            )}>
            {note}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The verdict WITHOUT its criteria list: a static badge, for
 * `SurfaceDifferencePanel`'s collapsed header summary.
 *
 * Not `VerdictChip` with the popover suppressed — the summary is rendered
 * inside `DockPanel`'s collapse toggle, which is a `<button>`, and a button
 * inside a button is invalid HTML that browsers flatten unpredictably. So this
 * is a `<span>`, and it deliberately cannot be opened: expanding the card is
 * how you get to the criteria.
 *
 * Both the glyph and the colour come from the shared `SEVERITY` map, so the
 * badge a reader sees on a collapsed card and the chip they see when they
 * expand it can never disagree about which severity this is.
 *
 * No outer margin of its own: a badge that leads a row, follows a figure in a
 * header summary, or sits in a `gap-*` flex row each wants different spacing,
 * and a baked-in `ml-2` forced every non-header caller to cancel it. The call
 * site passes its spacing via `className`.
 */
export function VerdictBadge({ verdict, className }: { verdict: Verdict; className?: string }) {
  const { t } = useTranslation('project');
  const s = SEVERITY[verdict];

  return (
    <span
      data-testid='verdict-badge'
      className={cn('inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 font-medium', s.cls, className)}>
      <span aria-hidden='true'>{s.glyph}</span>
      {t(`expectation.verdict.${verdict}`)}
    </span>
  );
}
